import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';

interface PermissionRouteProps {
  /** The permission this group of routes needs. */
  permission: string;
  /** Where to send someone who does not hold it. */
  fallback?: string;
  children?: React.ReactNode;
}

/**
 * G3. The second of the two places the permission helper is used (the first is
 * the sidebar builder in Layout).
 *
 * RoleBasedRoute compares a role-name string out of localStorage, which is why
 * removing a permission from a role never stopped anyone reaching a page: they
 * still had the role name. This guard reads the grant instead, so the roles
 * page reaches the routes too.
 *
 * It sits INSIDE RoleBasedRoute, not instead of it: the role check answers
 * "which shell does this person belong to", and this answers "may they open
 * this surface". Both server routes keep their own checks; hiding a page in the
 * browser is a courtesy, never a boundary.
 */
const PermissionRoute: React.FC<PermissionRouteProps> = ({ permission, fallback = '/dashboard', children }) => {
  const { can, ready } = usePermissions();

  // Hold the render until /aai/me has resolved. Bouncing first and asking
  // later would throw a champion off their own page on every cold load.
  if (!ready) return null;
  if (!can(permission)) return <Navigate to={fallback} replace />;
  if (children) return <>{children}</>;
  return <Outlet />;
};

export default PermissionRoute;
